import { useEffect } from 'react';

interface StatusCodeProps {
  code: number;
}

/**
 * Component that sets the HTTP status code using meta tags.
 * This works with the .htaccess configuration to return proper status codes.
 */
const StatusCode: React.FC<StatusCodeProps> = ({ code }) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Directly set status in URL without affecting navigation
      try {
        // Only add the status parameter if it's not already there
        const url = new URL(window.location.href);
        
        if (!url.searchParams.has('status')) {
          url.searchParams.set('status', code.toString());
          
          // Use replaceState to update URL without reload
          window.history.replaceState(
            { ...window.history.state, statusCode: code },
            document.title,
            url.toString()
          );
        }
      } catch (error) {
        console.error('Error setting status in URL:', error);
      }
      
      // Set meta tags for search engines and crawlers
      try {
        // Status meta tag
        let statusMeta = document.querySelector('meta[http-equiv="status"]');
        if (!statusMeta) {
          statusMeta = document.createElement('meta');
          statusMeta.setAttribute('http-equiv', 'status');
          document.head.appendChild(statusMeta);
        }
        statusMeta.setAttribute('content', code.toString());
        
        // For 404s, also add refresh meta tag
        if (code === 404) {
          // Update page title
          document.title = '404 - Page Not Found | Rythm';
          
          // Add data attributes to body for CSS targeting
          document.body.setAttribute('data-error-code', '404');
          
          // Call any custom status code handlers
          try {
            if (typeof window.__setStatusCode === 'function') {
              window.__setStatusCode(404);
            }
          } catch (e) {
            console.error('Error in status code handler:', e);
          }
          
          // Check if we're in a development environment
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.warn('404 NOT FOUND: The requested page does not exist.');
          }
        }
      } catch (error) {
        console.error('Error setting status meta tags:', error);
      }
    }
    
    // No cleanup needed - we want the status code to persist
    return () => {};
  }, [code]);

  return null;
};

export default StatusCode; 